/** Module to start AWS stream transcription. */

import store from "../store";
import audioUtils from "./transcribe-audio-utils.js"; //For encoding audio data as PCM
import mic from "microphone-stream"; //Collect microphone input as a stream of raw bytes
import * as util_utf8_node from "@aws-sdk/util-utf8-node"; //Utilities for encoding and decoding UTF8
import { EventStreamMarshaller } from "@aws-sdk/eventstream-marshaller"; //For converting binary event stream messages to and from JSON
import { functions } from "@/firebase";

class AwsTranscribe {
  constructor(langCode) {
    this._micStream;
    this._inputSampleRate = 48000; // The sample rate your mic is producting
    this._transcribeSampleRate = 16000; //The sample rate you requested from Transcribe
    this._transcribeLanguageCode = langCode; //The language you want Transcribe to use
    this._websocket;
    this._eventStreamMarshaller = new EventStreamMarshaller(util_utf8_node.toUtf8, util_utf8_node.fromUtf8);
  }
  /**
   * Starts AWS-Stream-Transcription
   * @param {MediaStream} title - Audio stream to transcribe
   */
  async startTranscribe(mediaStream) {
    this._micStream = new mic();
    // Converting MediaStream to MicStream for compatibility
    this._micStream.setStream(mediaStream);

    this._micStream.on("format", (data) => {
      this._inputSampleRate = data.sampleRate;
    });

    const params = {
      languageCode: this._transcribeLanguageCode,
      sampleRate: this._transcribeSampleRate,
    };

    // Getting presigned transcribe websocket url from
    // cloud function. This authorizes to use stream transcribe.
    await functions
      .httpsCallable("getSignedTranscribeUrl")(params)
      .then((data) => {
        const url = data.data;
        try {
          // Connect to AWS-Stream-Transcribe websocket with the presigned URL.
          this._websocket = new WebSocket(url);
          this._websocket.binaryType = "arraybuffer";
        } catch (error) {
          console.error("File: aws-socket-transcribe.js. Transcribe Websocket Error: ", error);
        }
      })
      .catch((err) => {
        console.error("File: aws-socket-transcribe.js, getSignedTranscribeUrl() error.", err);
      });

    // Handling websocket open
    this._websocket.onopen = () => {
      this._micStream.on("data", (rawAudioChunk) => {
        // the audio stream is raw audio bytes. Transcribe expects PCM with additional metadata, encoded as binary
        let binary = this.convertAudioToBinaryMessage(rawAudioChunk);

        if (this._websocket.readyState === this._websocket.OPEN) this._websocket.send(binary);
      });
    };

    // Handling websocket message
    this._websocket.onmessage = async (message) => {
      //convert the binary event stream message to JSON
      var messageWrapper = this._eventStreamMarshaller.unmarshall(Buffer(message.data));
      // var messageBody = JSON.parse(
      //   String.fromCharCode.apply(String, messageWrapper.body)
      // );
      var messageBody = JSON.parse(this.Utf8ArrayToStr(messageWrapper.body));

      let transcriptionResults = messageBody?.Transcript?.Results;
      if (transcriptionResults) {
        transcriptionResults.map((result) => {
          (result.Alternatives || []).map((alternative) => {
            const transcript = alternative.Items.map((item) => item.Content).join(" ");
            // console.log("result:", transcript);
            store.commit("mediasoup/set_transcriptionResult", transcript);
          });
        });
      } else {
        console.log("Got message from AWS Transcribe::", messageBody);
      }
    };

    // Handling websocket close event
    this._websocket.onclose = async (code, reason) => {
      console.log(`Transcribe socket closed. Code: ${code}. Reason: ${reason}`);
    };
  }

  closeConnection() {
    if (this._websocket) {
      this._websocket.close();
    }
  }

  convertAudioToBinaryMessage(audioChunk) {
    var raw = mic.toRaw(audioChunk);
    if (raw == null) return; // downsample and convert the raw audio bytes to PCM
    var downsampledBuffer = audioUtils.downsampleBuffer(raw, this._inputSampleRate, this._transcribeSampleRate);
    var pcmEncodedBuffer = audioUtils.pcmEncode(downsampledBuffer); // add the right JSON headers and structure to the message

    var audioEventMessage = this.getAudioEventMessage(Buffer.from(pcmEncodedBuffer)); //convert the JSON object + headers into a binary event stream message

    var binary = this._eventStreamMarshaller.marshall(audioEventMessage);
    return binary;
  }

  getAudioEventMessage(buffer) {
    // wrap the audio data in a JSON envelope
    return {
      headers: {
        ":message-type": {
          type: "string",
          value: "event",
        },
        ":event-type": {
          type: "string",
          value: "AudioEvent",
        },
      },
      body: buffer,
    };
  }

  Utf8ArrayToStr(array) {
    var out, i, len, c;
    var char2, char3;

    out = "";
    len = array.length;
    i = 0;
    while (i < len) {
      c = array[i++];
      switch (c >> 4) {
        case 0:
        case 1:
        case 2:
        case 3:
        case 4:
        case 5:
        case 6:
        case 7:
          // 0xxxxxxx
          out += String.fromCharCode(c);
          break;
        case 12:
        case 13:
          // 110x xxxx   10xx xxxx
          char2 = array[i++];
          out += String.fromCharCode(((c & 0x1f) << 6) | (char2 & 0x3f));
          break;
        case 14:
          // 1110 xxxx  10xx xxxx  10xx xxxx
          char2 = array[i++];
          char3 = array[i++];
          out += String.fromCharCode(((c & 0x0f) << 12) | ((char2 & 0x3f) << 6) | ((char3 & 0x3f) << 0));
          break;
      }
    }

    return out;
  }
}

export default AwsTranscribe;
